import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../store';
import {
  DashboardView,
  DashboardWidgets,
  WSDashboardView,
} from '@Helper/Interfaces';
import axios from 'axios';
import { DataManagement } from '@Helper/DataManagement';

const BaseURL = process.env.REACT_APP_MERGEFLO_API_URL ?? '';
const Version = process.env.REACT_APP_MERGEFLO_API_VERSION ?? '';

type InitialPostProps = {
  id: string;
  widgets: DashboardView[];
};

type InitialPostWSProps = {
  id: string;
  widgets: WSDashboardView[];
};

export const defaultDashboardElements = [
  {
    name: DashboardWidgets.Accomplishments,
    visible: true,
    order: 1,
  },
  {
    name: DashboardWidgets.ActionItems,
    visible: true,
    order: 2,
  },
  {
    name: DashboardWidgets.BurndownChart,
    visible: true,
    order: 3,
  },
  {
    name: DashboardWidgets.ConflictTracker,
    visible: true,
    order: 4,
  },
  {
    name: DashboardWidgets.DealBackground,
    visible: true,
    order: 5,
  },
  {
    name: DashboardWidgets.SankeyChart,
    visible: true,
    order: 6,
  },
  {
    name: DashboardWidgets.DependencyResolvedItems,
    visible: true,
    order: 7,
  },
  {
    name: DashboardWidgets.FreeText,
    visible: false,
    order: 1000000,
  },
  {
    name: DashboardWidgets.MilestoneProgressTracker,
    visible: true,
    order: 8,
  },

  {
    name: DashboardWidgets.RiskQuadrant,
    visible: true,
    order: 9,
  },
  {
    name: DashboardWidgets.UpcomingTasks,
    visible: true,
    order: 10,
  },
  {
    name: DashboardWidgets.WorkstreamStatus,
    visible: true,
    order: 11,
  },
];

export const defaultNonDashboardElements = [
  {
    name: DashboardWidgets.Accomplishments,
    visible: true,
    order: 1,
  },
  {
    name: DashboardWidgets.ActionItems,
    visible: true,
    order: 2,
  },
  {
    name: DashboardWidgets.BurndownChart,
    visible: true,
    order: 3,
  },
  {
    name: DashboardWidgets.ConflictTracker,
    visible: true,
    order: 4,
  },
  {
    name: DashboardWidgets.DealBackground,
    visible: true,
    order: 5,
  },
  {
    name: DashboardWidgets.DependenciesIncoming,
    visible: true,
    order: 6,
  },
  {
    name: DashboardWidgets.DependenciesOutgoing,
    visible: true,
    order: 7,
  },
  {
    name: DashboardWidgets.DependencyResolvedItems,
    visible: true,
    order: 8,
  },
  {
    name: DashboardWidgets.FreeText,
    visible: false,
    order: 1000000,
  },
  {
    name: DashboardWidgets.MilestoneProgressTracker,
    visible: true,
    order: 9,
  },

  {
    name: DashboardWidgets.RiskQuadrant,
    visible: true,
    order: 10,
  },
  {
    name: DashboardWidgets.UpcomingTasks,
    visible: true,
    order: 11,
  },
  {
    name: DashboardWidgets.WorkstreamStatus,
    visible: true,
    order: 12,
  },
];

export interface IProps {
  defaultViewWidget: DashboardView[];
  dashboardViewWidget: DashboardView[];
  workstreamViewWidget: WSDashboardView[];
  executiveViewWidget: DashboardView[];
  dashboardLoading: boolean;
}

const initialState: IProps = {
  defaultViewWidget: [
    {
      name: DashboardWidgets.Accomplishments,
      visible: true,
      order: 0,
    },
    {
      name: DashboardWidgets.ActionItems,
      visible: true,
      order: 1,
    },
    {
      name: DashboardWidgets.BurndownChart,
      visible: true,
      order: 2,
    },
    {
      name: DashboardWidgets.ConflictTracker,
      visible: true,
      order: 3,
    },
    {
      name: DashboardWidgets.DealBackground,
      visible: true,
      order: 4,
    },
    {
      name: DashboardWidgets.DependenciesIncoming,
      visible: false,
      order: 5,
    },
    {
      name: DashboardWidgets.SankeyChart,
      visible: false,
      order: 5,
    },
    {
      name: DashboardWidgets.DependenciesOutgoing,
      visible: false,
      order: 6,
    },
    {
      name: DashboardWidgets.DependencyResolvedItems,
      visible: true,
      order: 7,
    },
    {
      name: DashboardWidgets.FreeText,
      visible: false,
      order: 1000000,
    },
    {
      name: DashboardWidgets.MilestoneProgressTracker,
      visible: true,
      order: 8,
    },

    {
      name: DashboardWidgets.RiskQuadrant,
      visible: true,
      order: 9,
    },
    {
      name: DashboardWidgets.UpcomingTasks,
      visible: true,
      order: 10,
    },
    {
      name: DashboardWidgets.WorkstreamStatus,
      visible: true,
      order: 11,
    },
  ],
  dashboardViewWidget: [],
  workstreamViewWidget: [],
  executiveViewWidget: [],
  dashboardLoading: true,
};

export const postDVWidget = createAsyncThunk(
  'dashboard/postDVWidget',
  async (initialPost: InitialPostProps, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.post(
      BaseURL +
        Version +
        'mergeflo/dealspace/' +
        initialPost.id +
        '/dashImoSet',
      {
        settings: {
          widgets: [...initialPost.widgets],
        },
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data.jsonData.widgets) {
      return response.data.jsonData.widgets;
    } else {
      return [];
    }
  }
);

export const postWorkstreamWidget = createAsyncThunk(
  'dashboard/postWorkstreamWidget',
  async (initialPost: InitialPostWSProps, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.post(
      BaseURL +
        Version +
        'mergeflo/dealspace/' +
        initialPost.id +
        '/dashWorkstreamSet',
      {
        settings: {
          widgets: [...initialPost.widgets],
        },
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data.jsonData.widgets) {
      return response.data.jsonData.widgets;
    } else {
      return [];
    }
  }
);

export const postExecutiveWidget = createAsyncThunk(
  'dashboard/postExecutiveWidget',
  async (initialPost: InitialPostProps, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.post(
      BaseURL +
        Version +
        'mergeflo/dealspace/' +
        initialPost.id +
        '/dashExecSet',
      {
        settings: {
          widgets: [...initialPost.widgets],
        },
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data.jsonData.widgets) {
      return response.data.jsonData.widgets;
    } else {
      return [];
    }
  }
);

export const dashboardSlice = createSlice({
  name: 'dashboardAssets',
  initialState,
  reducers: {
    postDashboardViewWidget: (
      state,
      action: PayloadAction<DashboardView[]>
    ) => {
      state.dashboardViewWidget = action.payload;
    },
    postWorkstreamViewWidget: (
      state,
      action: PayloadAction<WSDashboardView[]>
    ) => {
      state.workstreamViewWidget = action.payload;
    },
    postExecutiveViewWidget: (
      state,
      action: PayloadAction<DashboardView[]>
    ) => {
      state.executiveViewWidget = action.payload;
    },
    postDashboardLoading: (state, action: PayloadAction<boolean>) => {
      state.dashboardLoading = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(postDVWidget.pending, state => {
        state.dashboardLoading = true;
      })
      .addCase(postDVWidget.fulfilled, (state, action) => {
        state.dashboardViewWidget = action.payload;
        state.dashboardLoading = false;
      })
      .addCase(postWorkstreamWidget.pending, state => {
        state.dashboardLoading = true;
      })
      .addCase(postWorkstreamWidget.fulfilled, (state, action) => {
        state.workstreamViewWidget = action.payload;
        state.dashboardLoading = false;
      })
      .addCase(postExecutiveWidget.pending, state => {
        state.dashboardLoading = true;
      })
      .addCase(postExecutiveWidget.fulfilled, (state, action) => {
        state.executiveViewWidget = action.payload;
        state.dashboardLoading = false;
      });
  },
});

export const {
  postDashboardViewWidget,
  postWorkstreamViewWidget,
  postExecutiveViewWidget,
  postDashboardLoading,
} = dashboardSlice.actions;

export const getDashboardViewDetail = (state: RootState) =>
  state.dashboardReducer;

export default dashboardSlice.reducer;
